import moment from "moment-timezone";

/**
 * Ensures an IANA time zone identifier is valid.
 * Falls back to UTC if undefined, empty, or not in Area/Location format.
 *
 * @param {string} zone
 * @returns {string}
 */
export const cleanIanaZone = (zone) => {
  if (zone && typeof zone === "string" && zone.includes("/")) {
    try {
      if (moment.tz.zone(zone.trim())) {
        return zone.trim();
      }
    } catch (_) {
      // ignore
    }
  }
  return "UTC";
};

/**
 * Extracts the consultant's intended recurring wall-clock time (HH:mm)
 * from a stored schedule slot time in the consultant's IANA timezone.
 *
 * Handles:
 * - Simple time strings (e.g., "14:00", "14:00:00")
 * - Reference ISO strings stored using dummy date 2000-01-01 (e.g., "2000-01-01T13:00:00Z")
 * - Appointment-dated ISO strings (e.g., "2026-09-04T13:00:00.000Z")
 *
 * @param {string|Date} slotTime
 * @param {string} consultantZone
 * @returns {string} "HH:mm" (e.g. "14:00")
 */
export const getConsultantWallClockTime = (slotTime, consultantZone) => {
  if (!slotTime) return "";
  const zone = cleanIanaZone(consultantZone);

  // If already a plain "HH:mm" or "HH:mm:ss" string
  if (typeof slotTime === "string") {
    const timeMatch = slotTime.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (timeMatch) {
      const hh = timeMatch[1].padStart(2, "0");
      const mm = timeMatch[2];
      return `${hh}:${mm}`;
    }
  }

  try {
    // If it's an ISO timestamp, extract the UTC time portion (HH:mm:ss)
    // and interpret it on the standard reference date 2000-01-01 in the consultant's zone.
    const rawUtc = moment.utc(slotTime);
    if (!rawUtc.isValid()) return "";

    const timeString = rawUtc.format("HH:mm:ss");
    const referenceMoment = moment.utc(`2000-01-01T${timeString}Z`).tz(zone);
    return referenceMoment.format("HH:mm");
  } catch (_) {
    return "";
  }
};

/**
 * Constructs the true UTC ISO timestamp for a specific appointment date
 * given the consultant's recurring wall-clock time and IANA timezone.
 *
 * Automatically accounts for standard time vs daylight saving time (DST)
 * for that specific date in the consultant's region.
 *
 * @param {string} dateStr "YYYY-MM-DD"
 * @param {string} wallClockTime "HH:mm" (e.g. "14:00")
 * @param {string} consultantZone (e.g. "Europe/Rome")
 * @returns {string} UTC ISO 8601 string (e.g. "2026-09-04T12:00:00.000Z")
 */
export const getSlotUtcTimestamp = (dateStr, wallClockTime, consultantZone) => {
  if (!dateStr || !wallClockTime) return null;
  const zone = cleanIanaZone(consultantZone);
  const cleanDate = typeof dateStr === "string" ? dateStr.slice(0, 10) : moment(dateStr).format("YYYY-MM-DD");

  try {
    const zonedMoment = moment.tz(`${cleanDate} ${wallClockTime}`, "YYYY-MM-DD HH:mm", zone);
    if (!zonedMoment.isValid()) return null;
    return zonedMoment.toISOString();
  } catch (_) {
    return null;
  }
};

/**
 * Normalizes an array of appointment slots returned from backend `/appointment/slot-availibility`,
 * ensuring slot `startTime` and `endTime` reflect the accurate UTC ISO timestamps
 * for the selected appointment date in the consultant's IANA timezone.
 *
 * @param {Array} rawSlots Array of slot objects from backend API
 * @param {string} dateStr "YYYY-MM-DD"
 * @param {string} consultantZone Consultant's IANA timezone (e.g. "Europe/Rome")
 * @returns {Array} Normalized slots with exact UTC startTime/endTime
 */
export const normalizeAppointmentSlots = (rawSlots, dateStr, consultantZone) => {
  if (!Array.isArray(rawSlots) || rawSlots.length === 0) return [];
  const cleanDate = typeof dateStr === "string" ? dateStr.slice(0, 10) : moment(dateStr).format("YYYY-MM-DD");
  const zone = cleanIanaZone(consultantZone);

  return rawSlots.map((item) => {
    const sched = item?.dailySchedule?.scheduleTime;
    if (!sched || !sched.startTime) return item;

    const wallStart = getConsultantWallClockTime(sched.startTime, zone);
    const wallEnd = getConsultantWallClockTime(sched.endTime, zone);

    if (!wallStart) return item;

    const accurateStartTime = getSlotUtcTimestamp(cleanDate, wallStart, zone);
    const accurateEndTime = wallEnd ? getSlotUtcTimestamp(cleanDate, wallEnd, zone) : null;

    return {
      ...item,
      dailySchedule: {
        ...item.dailySchedule,
        scheduleTime: {
          ...sched,
          startTime: accurateStartTime || sched.startTime,
          endTime: accurateEndTime || sched.endTime,
        },
      },
    };
  });
};

/**
 * Formats a UTC ISO timestamp in any target IANA timezone (e.g. client or consultant timezone).
 * Automatically applies the exact standard / DST offset active on that date in that region.
 *
 * @param {string|Date} utcIsoString UTC timestamp
 * @param {string} targetZone IANA timezone (e.g. "Europe/Rome")
 * @param {string} formatPattern Moment format pattern (default "h:mm A")
 * @returns {string}
 */
export const formatTimeInZone = (utcIsoString, targetZone, formatPattern = "h:mm A") => {
  if (!utcIsoString) return "—";
  const zone = cleanIanaZone(targetZone);
  try {
    const m = moment.utc(utcIsoString).tz(zone);
    return m.isValid() ? m.format(formatPattern) : "—";
  } catch (_) {
    return "—";
  }
};

/**
 * Formats a date from a UTC ISO timestamp in a target IANA timezone.
 *
 * @param {string|Date} utcIsoString UTC timestamp
 * @param {string} targetZone IANA timezone (e.g. "Europe/Rome")
 * @param {string} formatPattern Moment format pattern (default "DD/MM/YYYY")
 * @returns {string}
 */
export const formatDateInZone = (utcIsoString, targetZone, formatPattern = "DD/MM/YYYY") => {
  if (!utcIsoString) return "";
  const zone = cleanIanaZone(targetZone);
  try {
    const m = moment.utc(utcIsoString).tz(zone);
    return m.isValid() ? m.format(formatPattern) : "";
  } catch (_) {
    return "";
  }
};

/**
 * Returns the UTC offset in minutes for a given IANA timezone on a specific date.
 * E.g. for Europe/Rome on 2026-09-04 (CEST) -> 120 (+2h), on 2026-01-09 (CET) -> 60 (+1h).
 *
 * @param {string} targetZone IANA timezone (e.g. "Europe/Rome")
 * @param {string} forDate "YYYY-MM-DD"
 * @returns {number} Offset in minutes (e.g. +120, +60, -300)
 */
export const getZoneOffsetMinutesForDate = (targetZone, forDate) => {
  const zone = cleanIanaZone(targetZone);
  try {
    if (forDate) {
      const cleanDate = typeof forDate === "string" ? forDate.slice(0, 10) : moment(forDate).format("YYYY-MM-DD");
      return moment.tz(cleanDate, zone).utcOffset();
    }
    return moment.tz(zone).utcOffset();
  } catch (_) {
    return 0;
  }
};

/**
 * Determines whether an appointment is an Instant Call (direct access)
 * vs a Scheduled Call (indirect booked appointment).
 *
 * @param {Object} appt Appointment object
 * @returns {boolean}
 */
export const isInstantAppointment = (appt) => {
  if (!appt) return false;
  const raw = appt?.appointmentData || appt;
  const serviceSlug =
    raw?.service?.service?.slug ||
    raw?.service?.slug ||
    raw?.serviceData?.slug ||
    (typeof raw?.service === "string" ? raw.service : "") ||
    "";

  if (serviceSlug === "direct-access-online-psychological-counseling") {
    return true;
  }

  const serviceTitle = String(
    raw?.service?.service?.title?.en ||
    raw?.service?.service?.title ||
    raw?.service?.title?.en ||
    raw?.service?.title ||
    ""
  ).toLowerCase();

  if (serviceTitle.includes("direct-access") || serviceTitle.includes("direct access")) {
    return true;
  }

  return false;
};

/**
 * Resolves an appointment's canonical schedule date, UTC start ISO, UTC end ISO,
 * and user-facing formatted date / time representations.
 *
 * Handles all database representations:
 * - Full ISO timestamps on the appointment date (e.g. "2026-09-22T14:00:00.000Z")
 * - Reference ISO timestamps on dummy date 2000-01-01 (e.g. "2000-01-01T14:00:00.000Z")
 * - Plain time strings (e.g. "14:00", "14:00:00")
 * - Missing end times (defaults to start + 60 mins)
 *
 * @param {Object} appt Appointment object
 * @param {string} userTz User's IANA timezone (e.g. "Europe/Rome")
 * @returns {Object} Resolved schedule object
 */
export const resolveAppointmentSchedule = (appt, userTz = "UTC") => {
  const zone = cleanIanaZone(userTz);
  const details = appt?.details || appt?.appointmentData?.details || {};
  const rawAppt = appt?.appointmentData || appt || {};

  // 1. Extract raw schedule fields
  const rawDate =
    details?.appointmentDate ||
    rawAppt?.appointmentDate ||
    appt?.appointmentDate ||
    "";
  const rawStart =
    details?.appointmentStartTime ||
    rawAppt?.appointmentStartTime ||
    appt?.appointmentStartTime ||
    "";
  const rawEnd =
    details?.appointmentEndTime ||
    rawAppt?.appointmentEndTime ||
    appt?.appointmentEndTime ||
    "";

  // 2. Identify the civil appointment date "YYYY-MM-DD"
  let cleanDate = "";
  if (rawDate) {
    if (rawDate instanceof Date) {
      cleanDate = moment(rawDate).format("YYYY-MM-DD");
    } else if (typeof rawDate === "string" && rawDate.trim()) {
      const trimmed = rawDate.trim();
      const isoMatch = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
      if (isoMatch) {
        cleanDate = `${isoMatch[1]}-${isoMatch[2].padStart(2, "0")}-${isoMatch[3].padStart(2, "0")}`;
      } else {
        const dmyMatch = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
        if (dmyMatch) {
          cleanDate = `${dmyMatch[3]}-${dmyMatch[2].padStart(2, "0")}-${dmyMatch[1].padStart(2, "0")}`;
        } else {
          const mDate = moment.utc(trimmed);
          if (mDate.isValid() && mDate.year() > 2020) {
            cleanDate = mDate.tz(zone).format("YYYY-MM-DD");
          }
        }
      }
    }
  }

  // If cleanDate is empty or is a legacy dummy reference date (year <= 2020), derive from start / end
  if ((!cleanDate || parseInt(cleanDate.slice(0, 4), 10) <= 2020) && rawStart && typeof rawStart === "string") {
    const mStart = moment.utc(rawStart);
    if (mStart.isValid() && mStart.year() > 2020) {
      cleanDate = mStart.tz(zone).format("YYYY-MM-DD");
    }
  }
  if ((!cleanDate || parseInt(cleanDate.slice(0, 4), 10) <= 2020) && rawEnd && typeof rawEnd === "string") {
    const mEnd = moment.utc(rawEnd);
    if (mEnd.isValid() && mEnd.year() > 2020) {
      cleanDate = mEnd.tz(zone).format("YYYY-MM-DD");
    }
  }

  // 3. Resolve Start Time UTC ISO
  let startUtcIso = null;
  if (rawStart) {
    if (typeof rawStart === "string") {
      const timeMatch = rawStart.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      if (timeMatch && cleanDate) {
        const wallClock = `${timeMatch[1].padStart(2, "0")}:${timeMatch[2]}`;
        startUtcIso = getSlotUtcTimestamp(cleanDate, wallClock, zone);
      } else {
        const mStart = moment.utc(rawStart);
        if (mStart.isValid()) {
          if (mStart.year() > 2020 && (!cleanDate || rawStart.includes(cleanDate))) {
            startUtcIso = mStart.toISOString();
          } else if (cleanDate) {
            const wallClock = getConsultantWallClockTime(rawStart, zone);
            if (wallClock) {
              startUtcIso = getSlotUtcTimestamp(cleanDate, wallClock, zone);
            } else {
              startUtcIso = mStart.toISOString();
            }
          } else {
            startUtcIso = mStart.toISOString();
          }
        }
      }
    } else if (rawStart instanceof Date) {
      startUtcIso = rawStart.toISOString();
    }
  }

  // 4. Resolve End Time UTC ISO
  let endUtcIso = null;
  if (rawEnd) {
    if (typeof rawEnd === "string") {
      const timeMatch = rawEnd.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      if (timeMatch && cleanDate) {
        const wallClock = `${timeMatch[1].padStart(2, "0")}:${timeMatch[2]}`;
        endUtcIso = getSlotUtcTimestamp(cleanDate, wallClock, zone);
      } else {
        const mEnd = moment.utc(rawEnd);
        if (mEnd.isValid()) {
          if (mEnd.year() > 2020 && (!cleanDate || rawEnd.includes(cleanDate))) {
            endUtcIso = mEnd.toISOString();
          } else if (cleanDate) {
            const wallClock = getConsultantWallClockTime(rawEnd, zone);
            if (wallClock) {
              endUtcIso = getSlotUtcTimestamp(cleanDate, wallClock, zone);
            } else {
              endUtcIso = mEnd.toISOString();
            }
          } else {
            endUtcIso = mEnd.toISOString();
          }
        }
      }
    } else if (rawEnd instanceof Date) {
      endUtcIso = rawEnd.toISOString();
    }
  }

  // Fallback: If start exists but not end, default to start + 60 minutes
  if (!endUtcIso && startUtcIso) {
    endUtcIso = moment.utc(startUtcIso).add(60, "minutes").toISOString();
  }

  // Fallback: If only cleanDate exists
  if (!startUtcIso && cleanDate) {
    startUtcIso = moment.tz(cleanDate, zone).startOf("day").utc().toISOString();
  }
  if (!endUtcIso && cleanDate) {
    endUtcIso = moment.tz(cleanDate, zone).endOf("day").utc().toISOString();
  }

  // 5. Formatted display strings
  const displayDate = cleanDate
    ? moment.tz(cleanDate, "YYYY-MM-DD", zone).format("D MMMM YYYY")
    : (startUtcIso ? moment.utc(startUtcIso).tz(zone).format("D MMMM YYYY") : (appt?.date ? `${appt.date} ${appt.month || ""}` : "—"));

  const dayNumber = cleanDate
    ? moment.tz(cleanDate, "YYYY-MM-DD", zone).format("D")
    : (startUtcIso ? moment.utc(startUtcIso).tz(zone).format("D") : (appt?.date || "—"));

  const monthName = cleanDate
    ? moment.tz(cleanDate, "YYYY-MM-DD", zone).format("MMMM")
    : (startUtcIso ? moment.utc(startUtcIso).tz(zone).format("MMMM") : (appt?.month || "—"));

  let displayTimeRange = "—";
  if (startUtcIso && endUtcIso) {
    const startFmt = moment.utc(startUtcIso).tz(zone).format("h:mm A");
    const endFmt = moment.utc(endUtcIso).tz(zone).format("h:mm A");
    displayTimeRange = `${startFmt} - ${endFmt}`;
  } else if (startUtcIso) {
    displayTimeRange = moment.utc(startUtcIso).tz(zone).format("h:mm A");
  } else if (appt?.time) {
    displayTimeRange = appt.time;
  }

  // 6. Upcoming determination
  const status = String(rawAppt?.status || appt?.status || "").trim().toLowerCase();
  const isClosedOrCompleted = status === "close" || status === "completed" || status === "uncaptured";

  const isInstant = isInstantAppointment(appt);
  const consultantJoined =
    rawAppt?.consultantStatus === "join" ||
    appt?.consultantStatus === "join" ||
    Boolean(rawAppt?.consultantJoinTime) ||
    Boolean(appt?.consultantJoinTime);
  const sessionStarted = Boolean(rawAppt?.sessionStartTime || appt?.sessionStartTime);
  const isMissedOrNotStartedInstant = isInstant && (!consultantJoined || !sessionStarted);

  const now = moment.utc();
  let isUpcoming = true;
  if (isClosedOrCompleted || isMissedOrNotStartedInstant) {
    isUpcoming = false;
  } else if (endUtcIso) {
    isUpcoming = moment.utc(endUtcIso).isSameOrAfter(now.clone().subtract(15, "minutes"));
  } else if (startUtcIso) {
    isUpcoming = moment.utc(startUtcIso).isSameOrAfter(now.clone().subtract(60, "minutes"));
  } else if (cleanDate) {
    isUpcoming = moment.tz(cleanDate, zone).endOf("day").utc().isSameOrAfter(now);
  }

  return {
    cleanDate,
    startUtcIso,
    endUtcIso,
    displayDate,
    dayNumber,
    monthName,
    displayTimeRange,
    isUpcoming,
  };
};
